import { useEffect, useState } from "react";

import DashboardShell from "../components/common/DashboardShell";
import { LeagueApi } from "../utils/leagueApi";

const LEVELS = [
  { value: "local", label: "Local (same chapter)" },
  { value: "state", label: "State (same state)" },
  { value: "regional", label: "Regional (same region)" },
  { value: "national", label: "National (anywhere)" },
];

export default function RecruitBoard() {
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [team, setTeam] = useState("");
  const [level, setLevel] = useState("local");
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [opts, inv] = await Promise.all([
      LeagueApi.submissionOptions(),
      LeagueApi.listInvites(),
    ]);
    setTeams(opts.teams || []);
    setInvites(inv || []);
  };

  useEffect(() => {
    load()
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const loadPool = (teamId, lvl) => {
    setTeam(teamId);
    setLevel(lvl);
    if (!teamId) {
      setPool(null);
      return;
    }
    LeagueApi.recruitPool(teamId, lvl)
      .then(setPool)
      .catch(() => undefined);
  };

  const bypassJoin = async (inviteId) => {
    await LeagueApi.coMembershipJoin(inviteId).catch(() => undefined);
    load().catch(() => undefined);
  };

  return (
    <DashboardShell>
      <div className="mx-auto max-w-3xl space-y-8 p-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Recruit
        </h1>

        {loading ? (
          <p className="py-8 text-center text-gray-400 dark:text-gray-500">
            Loading…
          </p>
        ) : (
          <>
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
                Draft pool for your team
              </h2>
              {teams.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  You need to be on a team to recruit.
                </p>
              ) : (
                <div className="space-y-3">
                  <select
                    aria-label="Team"
                    className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                    value={team}
                    onChange={(e) => loadPool(e.target.value, level)}
                  >
                    <option value="">Select your team…</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Phase level"
                    className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                    value={level}
                    onChange={(e) => loadPool(team, e.target.value)}
                  >
                    {LEVELS.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {pool ? (
                <div className="mt-4">
                  <p className="mb-2 text-xs text-gray-400 dark:text-gray-500">
                    Free agents within the {pool.level} draft boundary
                  </p>
                  {pool.agents.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      No eligible free agents at this level.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {pool.agents.map((agent) => (
                        <li
                          key={agent.id}
                          className="rounded border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm"
                        >
                          <span className="font-medium text-gray-800 dark:text-gray-100">
                            {agent.name}
                          </span>
                          {agent.home_chapter ? (
                            <span className="text-gray-400 dark:text-gray-500">
                              {" "}
                              — {agent.home_chapter}
                            </span>
                          ) : null}
                          {agent.headline ? (
                            <span className="text-gray-500 dark:text-gray-400">
                              {" "}
                              · {agent.headline}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </section>

            <section>
              <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
                Your invites
              </h2>
              {invites.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No invites yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {invites.map((invite) => (
                    <li
                      key={invite.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm"
                    >
                      <span>
                        {invite.team_name}{" "}
                        <span className="text-gray-400 dark:text-gray-500">
                          ({invite.status})
                        </span>
                      </span>
                      {invite.status !== "locked" ? (
                        <button
                          className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                          type="button"
                          onClick={() => bypassJoin(invite.id)}
                        >
                          Join chapter &amp; accept slot
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
